import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { AltArrowDown, AltArrowUp, Logout2, Settings } from '../icons';
import Avatar from '../ui/Avatar';
import { useClickOutside } from '../../hooks';
import { setThemeMode, type ThemeMode, useThemeMode } from '../../../theme';

export interface AvatarUserData {
  name: string;
  roleName?: string;
}

interface ProfileProps {
  user: AvatarUserData | null;
  /** Avatar di sebelah nama (Figma Profile: instance Avatar, bisa disembunyikan). Default disembunyikan, seperti di Figma. */
  showAvatar?: boolean;
  onLogout?: () => void;
  settingsPath?: string;
  settingsLabel?: string;
  logoutLabel?: string;
  testId?: string;
}

const MODES: { value: ThemeMode; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Terang' },
  { value: 'dark', label: 'Gelap' },
];

/** Pilih tema terang/gelap/otomatis (disimpan per browser; lihat `theme.tsx`). */
function ThemeModeSwitch() {
  const mode = useThemeMode();
  return (
    <div role="radiogroup" aria-label="Tema" className="inline-flex gap-1">
      {MODES.map((m) => (
        <button
          key={m.value}
          type="button"
          role="radio"
          aria-checked={mode === m.value}
          onClick={() => setThemeMode(m.value)}
          className={[
            'rounded px-2 py-0.5 text-xs',
            mode === m.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
          ].join(' ')}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function Profile({
  user,
  showAvatar = false,
  onLogout,
  settingsPath = '/settings',
  settingsLabel,
  logoutLabel,
  testId,
}: ProfileProps) {
  const { t } = useTranslation();
  const resolvedSettingsLabel = settingsLabel ?? t('settings.title');
  const resolvedLogoutLabel = logoutLabel ?? t('auth.logout');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAbove, setDropdownAbove] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(
    useCallback(() => setDropdownOpen(false), []),
  );

  const firstName = user?.name.split(' ')[0] ?? 'User';
  const initial = user ? user.name.charAt(0).toUpperCase() : 'U';

  return (
    <div data-testid={testId} className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          if (!dropdownOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setDropdownAbove(window.innerHeight - rect.bottom < 200);
          }
          setDropdownOpen(!dropdownOpen);
        }}
        type="button"
        aria-expanded={dropdownOpen}
        data-state={dropdownOpen ? 'active' : undefined}
        className="wds-profile"
      >
        {showAvatar && <Avatar name={user?.name ?? initial} size="md" />}
        <div className="wds-profile__text text-center">
          <p className="wds-profile__title">Hi, {firstName}</p>
          <p className="wds-profile__support">{user?.roleName ?? 'User'}</p>
        </div>
        <span className="wds-profile__chevron">
          {dropdownOpen ? <AltArrowUp /> : <AltArrowDown />}
        </span>
      </button>

      {dropdownOpen && (
        <div
          role="menu"
          className={[
            'wds-menu wds-profile__menu absolute right-0 z-50 w-60',
            dropdownAbove ? 'bottom-full mb-2' : 'top-full mt-2',
          ].join(' ')}
        >
          <NavLink
            to={settingsPath}
            end
            role="menuitem"
            onClick={() => setDropdownOpen(false)}
            data-state="default"
            className={({ isActive }) => `wds-menu__item ${isActive ? 'wds-menu__item--current' : ''}`}
          >
            <span className="wds-menu__icon">
              <Settings />
            </span>
            <span className="wds-menu__text">{resolvedSettingsLabel}</span>
          </NavLink>
          <div className="wds-profile__language">
            <span>{t('theme.label', { defaultValue: 'Tema' })}</span>
            <ThemeModeSwitch />
          </div>
          {onLogout && (
            <button type="button" role="menuitem" data-state="destructive" onClick={onLogout} className="wds-menu__item">
              <span className="wds-menu__icon">
                <Logout2 />
              </span>
              <span className="wds-menu__text">{resolvedLogoutLabel}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Profile;
